import axiosInstance from '@/setup/axios/axiosInstance';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import {
  InventoryItemFormData,
  InventoryItemListResponse,
  InventoryItemPayload,
  InventoryItemQueryParams,
  InventoryItemRow,
} from '../models/ItemModel';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

const unwrapListResponse = (data: any): InventoryItemListResponse => {
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      page: 1,
      page_size: data.length || PAGE_SIZE,
      total_pages: 1,
    };
  }

  return {
    results: data?.results || [],
    count: data?.count || 0,
    page: data?.page || 1,
    page_size: data?.page_size || PAGE_SIZE,
    total_pages: data?.total_pages || 1,
  };
};

const toRow = (item: any): InventoryItemRow => ({
  id: String(item.id),
  code: item.code || '',
  name: item.name || '',
  item_type: item.item_type || '',
  item_type_label: item.item_type_label || item.item_type || '',
  supplier_id: item.supplier_id || '',
  supplier_name: item.supplier_name || '',
  manufacturer_id: item.manufacturer_id || '',
  manufacturer_name: item.manufacturer_name || '',
  group_id: item.group_id || '',
  group_name: item.group_name || '',
  uom_id: item.uom_id || '',
  uom_name: item.uom_name || '',
  status: item.status || 'approved',
  status_label: item.status_label || item.status || 'approved',
  company_id: item.company_id || '',
  company_name: item.company_name || '',
  specification: item.specification || '',
  track_lot: Boolean(item.track_lot),
  track_location: Boolean(item.track_location),
});

const toForm = (item: any): InventoryItemFormData & { id: string } => ({
  id: String(item.id),
  code: item.code || '',
  name: item.name || '',
  group_id: item.group_id || '',
  supplier_id: item.supplier_id || '',
  item_type: item.item_type || '',
  uom_id: item.uom_id || '',
  manufacturer_id: item.manufacturer_id || '',
  status: item.status || 'approved',
  company_id: item.company_id || '',
  specification: item.specification || '',
  track_lot: Boolean(item.track_lot),
  track_location: Boolean(item.track_location),
});

const toPayload = (payload: InventoryItemPayload) => ({
  code: payload.code.trim(),
  name: payload.name.trim(),
  company: payload.company_id,
  group_id: payload.group_id || null,
  supplier_id: payload.supplier_id || null,
  item_type: payload.item_type || '',
  uom_id: payload.uom_id || null,
  manufacturer_id: payload.manufacturer_id || null,
  specification: payload.specification.trim(),
  track_lot: payload.track_lot,
  track_location: payload.track_location,
});

export const getItems = async ({ search = '', status = 'all', companyId = '', page = 1, pageSize = PAGE_SIZE }: InventoryItemQueryParams = {}): Promise<InventoryItemListResponse> => {
  const params: Record<string, string | number> = {
    page,
    page_size: pageSize || PAGE_SIZE,
  };

  if (search.trim()) {
    params.search = search.trim();
  }
  if (status && status !== 'all') {
    params.status = status;
  }
  if (companyId) {
    params.company = companyId;
  }

  const response = await axiosInstance.get('/inventory/items/', { params });
  const parsed = unwrapListResponse(response.data);

  return {
    ...parsed,
    results: (parsed.results || []).map(toRow),
  };
};

export const getItem = async (itemId: string): Promise<InventoryItemFormData & { id: string }> => {
  const response = await axiosInstance.get(`/inventory/items/${itemId}/`);
  return toForm(response.data);
};

export const createItem = async (payload: InventoryItemPayload): Promise<InventoryItemRow> => {
  const response = await axiosInstance.post('/inventory/items/', toPayload(payload));
  return toRow(response.data);
};

export const updateItem = async (itemId: string, payload: InventoryItemPayload): Promise<InventoryItemRow> => {
  const response = await axiosInstance.patch(`/inventory/items/${itemId}/`, toPayload(payload));
  return toRow(response.data);
};

export const updateItemStatus = async (itemId: string, status: 'draft' | 'approved' | 'inactive'): Promise<InventoryItemRow> => {
  const response = await axiosInstance.patch(`/inventory/items/${itemId}/`, { status });
  return toRow(response.data);
};

export const deleteItem = async (itemId: string): Promise<void> => {
  await axiosInstance.delete(`/inventory/items/${itemId}/`);
};